import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { trackOnboardingEvent } from "./trackEvent";
import "./joyride.css";

function renderTooltipContent(content) {
  if (typeof content !== "string") return content;

  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={`strong-${index}`}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
  });
}

function getVisibleTarget(selector) {
  const elements = Array.from(document.querySelectorAll(selector));

  return elements.find((element) => {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      Number(style.opacity) !== 0 &&
      rect.width > 0 &&
      rect.height > 0
    );
  }) || null;
}

export const heLocale = {
  back: "< הקודם",
  close: "סגור את הסיור",
  last: "סיום ✓",
  next: "הבא >",
  skip: "דלג על הסיור",
};

export const bmcJoyrideStyles = {
  options: {
    primaryColor: "#E02020",
    backgroundColor: "#FFFFFF",
    textColor: "#0F0F0F",
    arrowColor: "#FFFFFF",
    overlayColor: "rgba(0, 0, 0, 0.55)",
    spotlightShadow: "0 0 0 3px #E02020",
    zIndex: 10000,
    width: 320,
  },
};

export default function JoyrideTour({
  run,
  steps,
  stepIndex,
  tourName,
  onEnd,
  onStepChange,
}) {
  const [overlay, setOverlay] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({
    top: 0,
    left: 0,
    maxHeight: "calc(100dvh - 24px)",
  });

  useEffect(() => {
    if (!run || !steps || steps.length === 0 || stepIndex >= steps.length) {
      setOverlay(null);
      return;
    }

    const updateOverlay = () => {
      const step = steps[stepIndex];
      const target = getVisibleTarget(step.target);

      if (!target) {
        console.warn("[JoyrideTour] target:not_found", {
          tourName,
          stepIndex,
          target: step.target,
        });
      // Target לא נמצא, עבור לשלב הבא
      if (stepIndex + 1 < steps.length) {
        if (onStepChange) onStepChange(stepIndex + 1);
      } else {
        handleTourEnd();
      }
        return;
      }

      const rect = target.getBoundingClientRect();

    // חישוב מיקום ה-tooltip לפי placement
      let top = rect.top;
      let left = rect.left;

      const placement = step.placement || "bottom";
      const viewportPadding = 12;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tooltipWidth = Math.min(
        viewportWidth - viewportPadding * 2,
        viewportWidth <= 768 ? 300 : 320
      );
      const estimatedTooltipHeight = Math.min(
        viewportHeight - viewportPadding * 2,
        viewportWidth <= 768 ? 240 : 260
      );
      const gap = 20;

    switch (placement) {
      case "bottom":
          top = rect.bottom + gap;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case "top":
          top = rect.top - estimatedTooltipHeight - gap;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case "left":
          top = rect.top + rect.height / 2 - estimatedTooltipHeight / 2;
          left = rect.left - tooltipWidth - gap;
        break;
      case "right":
          top = rect.top + rect.height / 2 - estimatedTooltipHeight / 2;
          left = rect.right + gap;
        break;
      default:
          top = rect.bottom + gap;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }

    // וודא שה-tooltip לא יוצא מהמסך
      const maxTop = Math.max(
        viewportPadding,
        viewportHeight - estimatedTooltipHeight - viewportPadding
      );
      top = Math.min(Math.max(viewportPadding, top), maxTop);

      if (left < viewportPadding) left = viewportPadding;
      if (left + tooltipWidth > viewportWidth - viewportPadding) {
        left = viewportWidth - tooltipWidth - viewportPadding;
      }

      setTooltipPos({
        top,
        left,
        maxHeight: `calc(100dvh - ${Math.ceil(top + viewportPadding)}px)`,
      });
      setOverlay({
        target: step.target,
        title: step.title,
        content: step.content,
        placement: step.placement,
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    };

    updateOverlay();
    window.addEventListener("resize", updateOverlay);
    window.addEventListener("scroll", updateOverlay, true);

    return () => {
      window.removeEventListener("resize", updateOverlay);
      window.removeEventListener("scroll", updateOverlay, true);
    };
  }, [run, steps, stepIndex, onEnd, onStepChange]);

  const handleTourEnd = () => {
    trackOnboardingEvent("tour_completed", { tourName });
    if (tourName === "catalog") {
      toast.success("🎉 סיימת את הסיור בקטלוג!");
    } else if (tourName === "vehicle") {
      toast.success("🎉 ההצעה נשלחה! תוכלו לעקוב אחריה בעמוד 'הצעות שלי'");
    } else if (tourName === "offers") {
      toast.success("🎉 עכשיו אתם מומחים בניהול הצעות!");
    }
    if (onEnd) onEnd("finished");
  };

  const handleNext = () => {
    trackOnboardingEvent("tour_step_completed", {
      tourName,
      stepId: `${tourName.charAt(0).toUpperCase()}-${String(stepIndex + 1).padStart(2, "0")}`,
    });

    if (stepIndex + 1 >= steps.length) {
      handleTourEnd();
      return;
    }

    // חפש את השלב הבא שיש לו אלמנט תקין
    let nextStep = stepIndex + 1;
    while (nextStep < steps.length) {
      const target = getVisibleTarget(steps[nextStep].target);
      if (target) {
        break;
      }
      nextStep++;
    }

    if (nextStep >= steps.length) {
      handleTourEnd();
      return;
    }

    if (onStepChange) onStepChange(nextStep);
  };

  const handlePrev = () => {
    if (stepIndex - 1 < 0) return;
    
    // חפש את השלב הקודם שיש לו אלמנט תקין
    let prevStep = stepIndex - 1;
    while (prevStep >= 0) {
      const target = getVisibleTarget(steps[prevStep].target);
      if (target) {
        break;
      }
      prevStep--;
    }

    if (prevStep >= 0 && onStepChange) onStepChange(prevStep);
  };

  const handleSkip = () => {
    trackOnboardingEvent("tour_skipped", { tourName, stepIndex });
    if (onEnd) onEnd("skipped");
  };

  if (!overlay) return null;

  const isLastStep = stepIndex + 1 >= steps.length;

  return (
    <>
      {/* Overlay */}
      <div className="joyride-overlay" />

      {/* Spotlight */}
      <div
        className="joyride-spotlight"
        style={{
          top: overlay.top - 5,
          left: overlay.left - 5,
          width: overlay.width + 10,
          height: overlay.height + 10,
        }}
      />

      {/* Tooltip */}
      <div
        className="joyride-tooltip"
        style={{
          top: `${tooltipPos.top}px`,
          left: `${tooltipPos.left}px`,
          maxHeight: tooltipPos.maxHeight,
        }}
      >
        <h3 className="joyride-tooltip__title">{overlay.title}</h3>
        <p className="joyride-tooltip__content">{renderTooltipContent(overlay.content)}</p>

        <div className="joyride-tooltip__footer">
          <span className="joyride-tooltip__progress">
            שלב {stepIndex + 1} מתוך {steps.length}
          </span>

          <div className="joyride-tooltip__buttons">
            <button
              onClick={handleSkip}
              className="joyride-button joyride-button--skip"
            >
              דלג
            </button>

            {stepIndex > 0 && (
              <button
                onClick={handlePrev}
                className="joyride-button joyride-button--back"
              >
                הקודם
              </button>
            )}

            <button
              onClick={handleNext}
              className="joyride-button joyride-button--next"
            >
              {isLastStep ? "סיום ✓" : "הבא >"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
